/**
 * Created by Ning Jiang on 2021-06-22.
 */

import {WinPresentationController} from "../../WinPresentationController";
import {gsap, TimelineLite} from "gsap";
import {SlotGame} from "@nolimitcity/slot-game/bin/SlotGame";
import {BigWinView} from "./BigWinView";
import {DDay} from "../../../DDay";
import {DDayGameConfig} from "../../../DDayGameConfig";
import {BelowStakeWinRestriction} from "../../../server/BelowStakeWinRestriction";
import {BlitzKriegSound} from "../../../sound/BlitzKriegSound";
import {GameHelper} from "../../../../core/utils/GameHelper";

export class BigWinController extends WinPresentationController {

    private _view: BigWinView;
    private _levelTimeline: TimelineLite | null;
    private _currentLevel: number;

    constructor() {
        super("BigWinController", true);
        this._view = new BigWinView();
    }

    protected hasWin(): boolean {
        return DDay.model.parsedGameData.currentWinRatioConfig!.bigWinLevel > -1;
    }

    protected async createWinAnimation(): Promise<any> {
        await new Promise<any>(resolve => {
            this._currentLevel = 0;
            BlitzKriegSound.fadeMusic(0, 400);
            this.playBigWinLevel(0, resolve);
        });
    }

    private playBigWinLevel(level: number, resolve: (value: any) => void): void {
        const data = DDay.model.parsedGameData;
        if (level > data.currentWinRatioConfig!.bigWinLevel) {
            this._levelTimeline = null;
            this._flowStateController.setSkipCallback();
            this.playEnding(data.currentWinRatioConfig!.bigWinLevel, resolve);
        } else {
            this._skippable = false;
            const isEndLevel = level === data.currentWinRatioConfig!.bigWinLevel;
            const currentRatio = DDayGameConfig.WIN_RATIOS.find(r => r.bigWinLevel === level)!;
            const from: number = level === 0 ? 0 : data.playedBetValue * currentRatio.ratio;
            const to: number = isEndLevel ? data.singleWin : data.playedBetValue * DDayGameConfig.WIN_RATIOS.find(r => r.bigWinLevel === (level + 1))!.ratio;
            const tooSmallEndLevel: boolean = isEndLevel && this.isLevelWinTooSmall(level, from, to);
            const tl = gsap.timeline();
            this._currentLevel = level;
            tl.add(this.getLevelAnimation(level, currentRatio.duration, from, to, isEndLevel));
            tl.add(() => this.playBigWinLevel(level + 1, resolve));
            this._levelTimeline = tl;
            this._flowStateController.setSkipCallback(() => this.skip!());
            if (tooSmallEndLevel) {
                this.skip();
            }
        }
    }

    private isLevelWinTooSmall(level: number, from: number, to: number): boolean {
        if (level === 0) {
            return false;
        }
        return (to - from) <= (from * 0.1);
    }

    private getLevelAnimation(level: number, duration: number, from: number, to: number, isEndLevel: boolean): TimelineLite {
        const tl = gsap.timeline();
        tl.add([
            () => {
                BlitzKriegSound.playTrack({name: BlitzKriegSound.BIG_WIN_SOUND[level]});
                DDay.bigWinCountUp.scale.set(1.2);
            },
            DDay.bigWinCountUp.getCountUpAnimation(from, to, duration),
            gsap.to(DDay.bigWinCountUp.scale, {duration: duration, x: 1, y: 1, ease: `power4.out`}),
            this._view.getLevelAnimation(level, duration, isEndLevel)
        ]);
        return tl
    }


    protected skip() {
        this._skippable = true;
        if (this._levelTimeline && this._levelTimeline.isActive() && this._skippable) {
            BlitzKriegSound.fadeTrack({
                name: BlitzKriegSound.BIG_WIN_SOUND[this._currentLevel],
                to: 0,
                duration: 500
            });
            this._levelTimeline.progress(1);
        }
    }

    private playEnding(endLevel: number, resolve: (value: any) => void): void {
        let tl: TimelineLite = new TimelineLite();
        tl.add(() => {
            BlitzKriegSound.playTrack({name: BlitzKriegSound.BIG_WIN_END});
            if (!DDay.model.gameData.brokeTheBank) {
                DDay.bigWinCountUp.updateBigWinWays(DDay.model.parsedGameData.totalWays);
            }
            SlotGame.keypadPlugin.setWin(DDay.model.parsedGameData.totalWin, DDay.model.parsedGameData.showTotalWin, BelowStakeWinRestriction.isWinBelowStake());
            this._view.playEnding(endLevel);
            if (!DDay.model.gameData.brokeTheBank) {
                BlitzKriegSound.fadeMusic(1, 1000);
            } else {
                BlitzKriegSound.stopTrack(BlitzKriegSound.MAIN_GAME_MUSIC);
                let isToggeled: boolean = false;

                BlitzKriegSound.fadeAndStopSound({name: "mainXbet", to: 0, from: isToggeled ? 1 : 0, duration: 0});
                BlitzKriegSound.playTrack({name: BlitzKriegSound.WIN_CAP_START});
                BlitzKriegSound.playTrackWithLoop({name: BlitzKriegSound.WIN_CAP_LOOP});
            }
        });
        tl.add(DDay.bigWinCountUp.stay());
        if (!DDay.model.gameData.brokeTheBank) {
            tl.add(DDay.bigWinCountUp.remove());
        }
        tl.add(() => {
            resolve(this);
        });
    }
}
